import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Calling from "../../api/Calling";
import NotFound from "../../components/ul/NotFound";
import {
  LuDownload,
  LuPlus,
  LuSquarePen,
  LuTrash2,
  LuCheck,
  LuX,
  LuTriangleAlert,
  LuSmartphone,
  LuCoins,
  LuChartNoAxesColumn,
} from "react-icons/lu";

const EMPTY_FORM = {
  play_url: "",
  pa_name: "",
  pa_package: "",
  pa_desc: "",
  pa_task: "install",
  pa_coin: "",
  pa_order: 0,
  pa_status: true,
  pa_icon_remote: "",
};

// What the user must do inside the promoted app before the reward fires.
const TASK_OPTIONS = [
  { value: "install", label: "Install", hint: "Install & open the app" },
  { value: "login", label: "Login", hint: "Install + register / log in" },
];

const statusStyles = {
  CLICKED: "bg-amber-500/15 text-amber-600",
  REDEEMED: "bg-teal-500/15 text-brand-teal",
  REJECTED: "bg-red-500/15 text-main-delete",
};

const PromoApps = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [fetching, setFetching] = useState(false);
  const [report, setReport] = useState(null);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getPromoApps();
      if (resp?.status) setData(resp?.data || []);
      else toast.error(resp?.message);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(false);
    }
  };

  const fetchReport = async () => {
    try {
      const resp = await Calling.getReferralInstalls({ limit: 15 });
      if (resp?.status) setReport(resp);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();
    fetchReport();
  }, []);

  const activeCount = useMemo(
    () => data.filter((a) => a.pa_status).length,
    [data]
  );

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIconFile(null);
    setIconPreview("");
    setBannerFile(null);
    setBannerPreview("");
    setShowForm(true);
  };

  const openEdit = (app) => {
    setEditingId(app._id);
    setForm({
      play_url: `https://play.google.com/store/apps/details?id=${app.pa_package}`,
      pa_name: app.pa_name || "",
      pa_package: app.pa_package || "",
      pa_desc: app.pa_desc || "",
      pa_task: app.pa_task || "install",
      pa_coin: app.pa_coin ?? "",
      pa_order: app.pa_order ?? 0,
      pa_status: app.pa_status,
      pa_icon_remote: "",
    });
    setIconFile(null);
    setIconPreview(app.pa_icon || "");
    setBannerFile(null);
    setBannerPreview(app.pa_banner || "");
    setShowForm(true);
  };

  // Reads what it can from the Play Store listing. The package name always
  // comes back (parsed from the URL); name and icon are best-effort.
  const handleFetchMeta = async () => {
    if (!form.play_url.trim()) return toast.error("Paste the Play Store link first");
    setFetching(true);
    try {
      const resp = await Calling.fetchPlayStoreMeta({ play_url: form.play_url.trim() });
      if (!resp?.status) return toast.error(resp?.message);

      const d = resp.data || {};
      setForm((f) => ({
        ...f,
        pa_package: d.pa_package || f.pa_package,
        pa_name: d.pa_name || f.pa_name,
        pa_icon_remote: d.pa_icon_remote || "",
      }));
      if (d.pa_icon_remote && !iconFile) setIconPreview(d.pa_icon_remote);

      if (d.fetched) toast.success("Details fetched");
      else toast.info(resp.message);
    } catch (e) {
      toast.error("Could not reach the Play Store");
    } finally {
      setFetching(false);
    }
  };

  const onPickIcon = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
    setField("pa_icon_remote", "");
  };

  const onPickBanner = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!form.pa_name.trim()) return toast.error("Enter the app name");
    if (!form.pa_package.trim()) return toast.error("Package name is required");
    if (form.pa_coin === "" || Number(form.pa_coin) < 0)
      return toast.error("Enter a valid reward");

    const fd = new FormData();
    fd.append("pa_name", form.pa_name.trim());
    fd.append("pa_package", form.pa_package.trim());
    fd.append("pa_desc", form.pa_desc.trim());
    fd.append("pa_task", form.pa_task);
    fd.append("pa_coin", Number(form.pa_coin));
    fd.append("pa_order", Number(form.pa_order) || 0);
    fd.append("pa_status", form.pa_status);
    if (form.pa_icon_remote) fd.append("pa_icon_remote", form.pa_icon_remote);
    if (iconFile) fd.append("image", iconFile);
    if (bannerFile) fd.append("banner", bannerFile);

    setLoader(true);
    try {
      const resp = editingId
        ? await Calling.updatePromoApp(editingId, fd)
        : await Calling.addPromoApp(fd);
      if (resp?.status) {
        toast.success(resp?.message);
        setShowForm(false);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } finally {
      setLoader(false);
    }
  };

  const toggleStatus = async (app) => {
    const fd = new FormData();
    fd.append("pa_status", !app.pa_status);
    const resp = await Calling.updatePromoApp(app._id, fd);
    if (resp?.status) fetchData();
    else toast.error(resp?.message);
  };

  const handleDelete = async (app) => {
    if (!window.confirm(`Delete "${app.pa_name}"? Past referral records are kept.`))
      return;
    setLoader(true);
    try {
      const resp = await Calling.deletePromoApp(app._id);
      if (resp?.status) {
        toast.success(resp?.message);
        fetchData();
      } else {
        toast.error(resp?.message);
      }
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="md:ml-56 px-4 sm:px-6 lg:px-8 mt-16 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 pb-5">
        <div className="flex items-center gap-4">
          <span className="p-3.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-2xl shadow-lg shadow-teal-500/30 flex items-center justify-center">
            <LuSmartphone className="text-2xl md:text-3xl" />
          </span>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-main-font tracking-tight leading-none">
              Promo Apps
            </h1>
            <p className="text-sm text-main-subfont mt-1.5">
              {data.length} app{data.length === 1 ? "" : "s"} · {activeCount} live in the app
            </p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="btn-teal flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition hover:-translate-y-0.5"
        >
          <LuPlus size={18} /> Add app
        </button>
      </div>

      {/* Summary */}
      {report?.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Links shared", value: report.summary.CLICKED + report.summary.REDEEMED + report.summary.REJECTED },
            { label: "Installs rewarded", value: report.summary.REDEEMED },
            { label: "Blocked (fraud)", value: report.summary.REJECTED },
            { label: "Coins paid out", value: report.summary.coins_paid },
          ].map((s) => (
            <div key={s.label} className="ui-card px-4 py-3.5">
              <p className="text-xs text-main-subfont">{s.label}</p>
              <p className="text-2xl font-extrabold text-main-font mt-1">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* App list */}
      <div className="ui-card-solid overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-main-th text-left">
                <th className="px-4 py-3 font-semibold text-main-font">App</th>
                <th className="px-4 py-3 font-semibold text-main-font">Banner</th>
                <th className="px-4 py-3 font-semibold text-main-font">Package name</th>
                <th className="px-4 py-3 font-semibold text-main-font">Task</th>
                <th className="px-4 py-3 font-semibold text-main-font">Reward</th>
                <th className="px-4 py-3 font-semibold text-main-font">Order</th>
                <th className="px-4 py-3 font-semibold text-main-font">Live</th>
                <th className="px-4 py-3 font-semibold text-main-font text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((app) => (
                <tr key={app._id} className="border-t border-main-border hover:bg-main-th">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {app.pa_icon ? (
                        <img
                          src={app.pa_icon}
                          alt=""
                          className="w-10 h-10 rounded-xl object-cover bg-white border border-main-border shrink-0"
                        />
                      ) : (
                        <span className="w-10 h-10 rounded-xl bg-teal-500/10 text-brand-teal flex items-center justify-center shrink-0">
                          <LuSmartphone size={18} />
                        </span>
                      )}
                      <div>
                        <p className="font-semibold text-main-font">{app.pa_name}</p>
                        {app.pa_desc && (
                          <p className="text-xs text-main-subfont line-clamp-1">{app.pa_desc}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {app.pa_banner ? (
                      <img
                        src={app.pa_banner}
                        alt=""
                        className="w-20 h-9 rounded-lg object-cover border border-main-border"
                      />
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                        <LuTriangleAlert size={13} /> Missing
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs text-main-subfont break-all">{app.pa_package}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                        app.pa_task === "login"
                          ? "bg-violet-500/15 text-violet-600"
                          : "bg-sky-500/15 text-sky-600"
                      }`}
                    >
                      {app.pa_task === "login" ? "Login" : "Install"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-brand-teal">
                      <LuCoins size={15} /> {app.pa_coin}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-main-subfont">{app.pa_order}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleStatus(app)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
                        app.pa_status
                          ? "bg-teal-500/15 text-brand-teal"
                          : "bg-main-bg text-main-subfont border border-main-border"
                      }`}
                    >
                      {app.pa_status ? "Live" : "Hidden"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(app)}
                        className="p-2 rounded-lg text-brand-teal hover:bg-teal-500/10 transition"
                        title="Edit"
                      >
                        <LuSquarePen size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(app)}
                        className="p-2 rounded-lg text-main-delete hover:bg-main-delete/10 transition"
                        title="Delete"
                      >
                        <LuTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loader && data.length === 0 && (
          <div className="py-10">
            <NotFound />
          </div>
        )}
      </div>

      {/* Install activity */}
      <div className="flex items-center gap-3 mt-10 mb-4">
        <span className="p-2.5 bg-gradient-to-br from-teal-500 to-cyan-500 text-white rounded-xl flex items-center justify-center">
          <LuChartNoAxesColumn className="text-lg" />
        </span>
        <div>
          <h2 className="text-lg font-extrabold text-main-font leading-none">Install activity</h2>
          <p className="text-xs text-main-subfont mt-1">Latest install links and what happened to them</p>
        </div>
      </div>

      <div className="ui-card-solid overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-main-th text-left">
                <th className="px-4 py-3 font-semibold text-main-font">User</th>
                <th className="px-4 py-3 font-semibold text-main-font">App</th>
                <th className="px-4 py-3 font-semibold text-main-font">Coins</th>
                <th className="px-4 py-3 font-semibold text-main-font">Status</th>
                <th className="px-4 py-3 font-semibold text-main-font">Shared on</th>
              </tr>
            </thead>
            <tbody>
              {(report?.data || []).map((row) => (
                <tr key={row._id} className="border-t border-main-border hover:bg-main-th">
                  <td className="px-4 py-3">
                    <p className="font-medium text-main-font">{row.u_info?.u_name || "—"}</p>
                    <p className="text-xs text-main-subfont">{row.u_info?.u_email || ""}</p>
                  </td>
                  <td className="px-4 py-3 text-main-subfont">{row.pa_name || row.pa_package}</td>
                  <td className="px-4 py-3 text-main-font">{row.pa_coin}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${statusStyles[row.status]}`}>
                      {row.status === "CLICKED" ? "Waiting" : row.status === "REDEEMED" ? "Rewarded" : "Blocked"}
                    </span>
                    {row.reject_reason && (
                      <p className="text-[11px] text-main-subfont mt-1">{row.reject_reason}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-main-subfont">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(!report?.data || report.data.length === 0) && (
          <div className="py-10">
            <NotFound />
          </div>
        )}
      </div>

      {/* Add / edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4 py-10">
          <div className="ui-card-solid w-full max-w-2xl p-6">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-extrabold text-main-font">
                {editingId ? "Edit promo app" : "Add promo app"}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 rounded-lg text-main-subfont hover:bg-main-bg transition"
              >
                <LuX size={18} />
              </button>
            </div>
            <p className="text-sm text-main-subfont mb-5">
              Paste the Play Store link and the rest fills itself
            </p>

            {/* Play Store URL */}
            <label className="block text-sm font-medium text-main-font mb-1.5">
              Play Store URL
            </label>
            <div className="flex gap-2 mb-5">
              <input
                className="ui-input flex-1"
                placeholder="https://play.google.com/store/apps/details?id=com.company.app"
                value={form.play_url}
                onChange={(e) => setField("play_url", e.target.value)}
              />
              <button
                onClick={handleFetchMeta}
                disabled={fetching}
                className="btn-teal flex items-center gap-2 px-4 rounded-xl text-sm font-semibold whitespace-nowrap disabled:opacity-60"
              >
                <LuDownload size={16} /> {fetching ? "Fetching..." : "Fetch"}
              </button>
            </div>

            <div className="border-t border-main-border pt-5 mb-5">
              <div className="flex flex-col sm:flex-row gap-5">
                {/* Icon */}
                <div className="shrink-0">
                  <label className="block text-sm font-medium text-main-font mb-1.5">Icon</label>
                  <label className="w-20 h-20 rounded-2xl border border-main-border bg-white flex items-center justify-center cursor-pointer overflow-hidden hover:border-brand-teal transition">
                    {iconPreview ? (
                      <img src={iconPreview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-brand-teal">
                        <LuPlus size={22} />
                      </span>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={onPickIcon} />
                  </label>
                </div>

                <div className="flex-1 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-main-font mb-1.5">App name</label>
                    <input
                      className="ui-input"
                      placeholder="Upside Cash"
                      value={form.pa_name}
                      onChange={(e) => setField("pa_name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-main-font mb-1.5">
                      Package name
                    </label>
                    <input
                      className="ui-input font-mono text-sm"
                      placeholder="com.company.app"
                      value={form.pa_package}
                      onChange={(e) => setField("pa_package", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* The one field that fails silently if it is wrong */}
            <div className="flex items-start gap-2.5 bg-amber-500/10 rounded-xl px-3.5 py-3 mb-5">
              <LuTriangleAlert className="text-amber-600 shrink-0 mt-0.5" size={16} />
              <p className="text-xs text-amber-700 leading-relaxed">
                Package name must match the target app's applicationId exactly. One typo and no
                reward will ever fire — and no error will show.
              </p>
            </div>

            {/* Wide banner — the artwork the app shows on the HOME screen for this promo */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-main-font mb-1.5">
                Home banner <span className="text-main-subfont font-normal">(wide, shown on the app's home screen)</span>
              </label>
              <label className="block w-full h-28 rounded-2xl border border-dashed border-main-border bg-main-bg flex items-center justify-center cursor-pointer overflow-hidden hover:border-brand-teal transition">
                {bannerPreview ? (
                  <img src={bannerPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="flex flex-col items-center gap-1 text-main-subfont text-xs">
                    <LuPlus size={20} className="text-brand-teal" />
                    Upload banner (1024 x 500 works best)
                  </span>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={onPickBanner} />
              </label>
            </div>

            {/* Task type — what earns the reward */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-main-font mb-1.5">Task</label>
              <div className="grid grid-cols-2 gap-2">
                {TASK_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setField("pa_task", opt.value)}
                    className={`rounded-xl border px-4 py-3 text-left transition ${
                      form.pa_task === opt.value
                        ? "border-brand-teal bg-teal-500/10"
                        : "border-main-border hover:bg-main-bg"
                    }`}
                  >
                    <span
                      className={`block text-sm font-semibold ${
                        form.pa_task === opt.value ? "text-brand-teal" : "text-main-font"
                      }`}
                    >
                      {opt.label}
                    </span>
                    <span className="block text-xs text-main-subfont mt-0.5">{opt.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-sm font-medium text-main-font mb-1.5">
                  Reward coins
                </label>
                <input
                  type="number"
                  min="0"
                  className="ui-input"
                  placeholder="100"
                  value={form.pa_coin}
                  onChange={(e) => setField("pa_coin", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-main-font mb-1.5">
                  Display order
                </label>
                <input
                  type="number"
                  className="ui-input"
                  value={form.pa_order}
                  onChange={(e) => setField("pa_order", e.target.value)}
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-main-font mb-1.5">Description</label>
              <input
                className="ui-input"
                placeholder="Install and reach home screen to earn"
                value={form.pa_desc}
                onChange={(e) => setField("pa_desc", e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between border-t border-main-border pt-5">
              <label className="flex items-center gap-2.5 text-sm text-main-font cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.pa_status}
                  onChange={(e) => setField("pa_status", e.target.checked)}
                  className="w-4 h-4 accent-brand-teal"
                />
                Show in app
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium border border-main-border text-main-subfont hover:bg-main-bg transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loader}
                  className="btn-teal flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                >
                  <LuCheck size={17} /> {editingId ? "Save changes" : "Save app"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoApps;
